import { useParams, useNavigate } from 'react-router-dom'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { ArrowLeft } from 'lucide-react'
import { useProductsOpenApi as useRelatedProductsApi } from 'src/api/db/apiProductsOpen'
import { PageContainer } from 'src/shared/components/PageContainer'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Footer } from 'src/shared/components/Footer'
import { useProductData } from './hooks/useProductData'
import { useProductLikes } from './hooks/useProductLikes'
import { QuantitySelector } from 'src/shared/components/ProductGrid/ProductCard/ProductPreview/components/QuantitySelector'
import { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import { useEffect, useRef, useState } from 'react'
import { ProductLoadingSkeleton } from './components/ProductLoadingSkeleton'
import { ProductNotFound } from './components/ProductNotFound'
import { ProductHeader } from 'src/shared/components/ProductShared/ProductHeader'
import { ProductPriceDisplay } from 'src/shared/components/ProductShared/ProductPriceDisplay'
import { ProductLikeButton } from 'src/shared/components/ProductShared/ProductLikeButton'
import { KlayderRecommendsSection } from 'src/shared/components/ProductShared/KlayderRecommendsSection'
import { ProductDescription } from 'src/shared/components/ProductShared/ProductDescription'
import { ProductCategories } from 'src/shared/components/ProductShared/ProductCategories'
import { TabButton } from 'src/shared/components/ProductGrid/ProductCard/ProductPreview/components/TabButton'
import { ProductImageCarousel } from 'src/shared/components/ProductGrid/ProductCard/ProductPreview/components/ProductImageCarousel'
import { ProductImageSection } from 'src/shared/components/ProductGrid/ProductCard/ProductPreview/components/ProductImageSection'
import { DecorationPicker } from 'src/pages/_myAccount/EditDecoration/components/DecorationPicker'
import { useGetDecorationInfo } from 'src/api/server/useGetDecorationInfo'
import { useQueryClient } from '@tanstack/react-query'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useProductsClosedApi } from 'src/api/db/apiProductsClosed'
import { queryKeys } from 'src/api/db/queryKeys'
import { DraftStatusBadge } from 'src/pages/_myAccount/Inventory/Products/AddEditProduct/components/DraftStatusBadge'
import { cn } from 'src/shared/components/ui/utils'
import { FavouriteProductButton } from 'src/shared/components/ProductShared/FavouriteProductButton'
import { useAuth } from 'src/shared/hooks/authProvider'

export const ProductPage = () => {
  const { product_id } = useParams<{ product_id: string }>()
  const navigate = useNavigate()
  const { t } = useLocalization()
  const { session } = useAuth()
  const loggedIn = !!session?.user?.email
  const cardRef = useRef<HTMLDivElement>(null)
  const [selectedSection, setSelectedSection] = useState('overview')

  const {
    product,
    isLoadingProduct,
    product_variant_colors,
    chosen_product_variant_color_,
    setProduct_variant_color,
    quantityInput,
    setQuantityInput,
  } = useProductData(product_id)

  const {
    likeCount,
    isLiked,
    isAuthenticated,
    toggleLike: handleToggleLike,
    toggleLikePending,
  } = useProductLikes(product?.id)

  const productName = t(product?.item_name_translations)
  const product_description = t(product?.item_description_translations)
  const price = product?.price ?? 0
  const moq = product?.moq || 1
  const isMobile = window.innerWidth < 640
  const variant = isMobile ? 'mobile' : 'desktop'

  useEffect(() => {
    if (!productName) return

    const previousTitle = document.title
    document.title = `${productName} | Klayder`

    return () => {
      document.title = previousTitle
    }
  }, [productName])

  const { productsByIdsOpen: related_products } = useRelatedProductsApi({
    // @ts-ignore
    article_numbers: product?.related_product_ids?.split(',') || [],
  })

  const { decorations } = useGetDecorationInfo({
    product_id: product?.id ?? Number(product_id ?? 0),
    item_quantity: moq || 1,
    item_colors: 1,
  })

  const queryClient = useQueryClient()
  const isAdmin = useIsAdmin()
  // Only the mutation is needed here; the product itself comes from product_open.
  const { updateProduct } = useProductsClosedApi({})

  const handleToggleDraft = async () => {
    if (product?.id) {
      await updateProduct.mutateAsync({
        id: product.id,
        is_draft: !product?.is_draft,
      })

      queryClient.invalidateQueries({ queryKey: [queryKeys.products] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.productsClosed] })
      if (product?.is_draft === false) {
        window.history.back()
      }
    }
  }

  if (isLoadingProduct) {
    return <ProductLoadingSkeleton />
  }

  if (!product) {
    return <ProductNotFound />
  }

  const productImages = chosen_product_variant_color_ ? (
    isMobile ? (
      <ProductImageCarousel
        chosen_product_variant_color_={
          chosen_product_variant_color_ as ProductVariantColor_
        }
        product_id={product.id}
      />
    ) : (
      <ProductImageSection
        chosen_product_variant_color_={
          chosen_product_variant_color_ as ProductVariantColor_
        }
        product_id={product.id}
      />
    )
  ) : null

  return (
    <div className="theme-bg min-h-dvh">
      <PageContainer
        width="lg"
        className={cn(isMobile && '!px-3 !py-3')}
      >
        <Row className="gap-2 items-center mb-3 sm:mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('Back', 'Tillbaka')}
          </Button>

          {isAdmin && (
            <DraftStatusBadge
              product={product}
              onToggle={handleToggleDraft}
              disabled={updateProduct.isPending}
            />
          )}
        </Row>

        <Column
          className={cn(
            isMobile
              ? 'relative gap-4 pb-8'
              : 'gap-10 !flex-row items-start'
          )}
        >
          {isMobile ? (
            productImages
          ) : (
            <div className="w-1/2 min-w-0">{productImages}</div>
          )}

          <div
            ref={cardRef}
            className={cn('min-w-0', !isMobile && 'w-1/2')}
          >
            <Column className={isMobile ? 'gap-4' : 'gap-6'}>
              <ProductHeader
                productName={productName}
                product_id={product.id}
                chosenColor={chosen_product_variant_color_?.color || ''}
                product_variant_colors={
                  (product_variant_colors ?? []) as ProductVariantColor_[]
                }
                scrollContainerRef={cardRef}
                setProduct_variant_color={setProduct_variant_color}
              />

              <ProductPriceDisplay price={price} moq={moq} variant={variant} />

              <Row className={cn('gap-2', !isMobile && 'flex-wrap')}>
                <ProductLikeButton
                  likeCount={likeCount}
                  isLiked={isLiked}
                  isAuthenticated={isAuthenticated}
                  onToggleLike={handleToggleLike}
                  isPending={toggleLikePending}
                  variant={variant}
                />
                <FavouriteProductButton
                  productId={product.id}
                  variant={variant}
                />
              </Row>

              <div className="relative min-w-0">
                <div className="w-full gap-6 p-0 overflow-x-auto scrollbar-hide bg-transparent flex">
                  <TabButton
                    selected={selectedSection === 'overview'}
                    onClick={() => setSelectedSection('overview')}
                  >
                    {t('Overview', 'Översikt')}
                  </TabButton>

                  <TabButton
                    selected={selectedSection === 'decorations'}
                    onClick={() => setSelectedSection('decorations')}
                    className="flex items-center"
                  >
                    {t('Decorations', 'Dekorationer')} (
                    {decorations?.length ?? 0})
                  </TabButton>

                  <TabButton
                    selected={selectedSection === 'other'}
                    onClick={() => setSelectedSection('other')}
                    className="flex items-center"
                  >
                    {t('Other', 'Övrigt')}
                  </TabButton>
                </div>
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
              </div>

              {selectedSection === 'overview' && (
                <>
                  <KlayderRecommendsSection
                    klayder_recommends={product.klayder_recommends ?? false}
                    klayder_recommendation_text={
                      product.klayder_recommendation_text ?? ''
                    }
                    related_products={related_products?.data as any}
                    onRelatedProductClick={(id) => navigate(`/product/${id}`)}
                    variant={variant}
                  />

                  <ProductDescription description={product_description} />

                  {loggedIn && (
                    <QuantitySelector
                      quantityInput={quantityInput}
                      setQuantityInput={setQuantityInput}
                      price={price}
                      moq={moq}
                      product_id={product.id}
                      chosen_product_variant_color_id={
                        chosen_product_variant_color_?.id
                      }
                      product_name={product.item_name ?? ''}
                    />
                  )}
                </>
              )}

              {selectedSection === 'decorations' && (
                <DecorationPicker
                  readonly
                  decorations={decorations}
                  quoteSubItem={undefined}
                  selectedPrintCode={null}
                  setPrintCode={() => {}}
                  withTitle={false}
                  withCard={false}
                  fullHeight
                />
              )}

              {selectedSection === 'other' && (
                <ProductCategories
                  categories={product.category_product}
                  articleNumber={product.article_number ?? ''}
                  variant={variant}
                />
              )}
            </Column>
          </div>
        </Column>
      </PageContainer>
      <Footer />
    </div>
  )
}
